"use client";

import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFLink from "@/Web/Components/TFLink/TFLink";
import apiCall from "@/libs/api";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { IoCubeSharp } from "react-icons/io5";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";

const ExtensionCard = ({
  extension,
  href,
}: {
  extension: APIDataHubExtension;
  href: string;
}) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-lg hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      <div className="relative w-full aspect-[4/3] rounded-t-lg bg-gradient-to-br from-black/5 to-black/10 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <IoCubeSharp className="w-16 h-16 text-black/25 group-hover:text-black/35 transition-colors" />
        </div>
        <TFBadge
          title={extension.category.title}
          className={`absolute top-2 ${isRTL ? "right-2" : "left-2"}`}
        />
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="text-base font-semibold text-black line-clamp-2 group-hover:text-black/70 transition-colors mb-2">
          {extension.title}
        </h3>

        <p className="text-sm text-black/60 mb-3 line-clamp-2 flex-grow">
          {extension.description}
        </p>

        <div className="flex flex-wrap gap-1.5 mt-auto">
          {extension.tags.slice(0, 3).map((tag) => (
            <span
              key={tag.ref}
              className="text-xs px-2 py-0.5 bg-black/10 rounded-full text-black/70"
            >
              {tag.title}
            </span>
          ))}
        </div>
      </div>
    </TFLink>
  );
};

export default function SectionExtensions() {
  const t = useTranslations("web.pages.home.sections.extensions");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [itemsPerView, setItemsPerView] = useState(3);
  const [extensions, setExtensions] = useState<APIDataHubExtension[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadExtensions = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/extensions/list", {
          locale: locale as UILocale,
        });
        setExtensions(response.extensions);
      } catch (error) {
        console.error("Failed to load extensions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadExtensions();
    }
  }, [locale]);

  const featuredExtensions = useMemo(() => {
    return extensions.slice(0, 12);
  }, [extensions]);

  if (isLoading || featuredExtensions.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allExtensions"),
        href: "/platform/hub/extensions",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [{ title: t("available"), value: extensions.length }],
        position: "left",
      }}
    >
      <TFHorizontalSlider
        items={featuredExtensions}
        renderItem={(extension) => (
          <ExtensionCard
            extension={extension}
            href={`/platform/hub/extensions/${extension.ref}`}
          />
        )}
        itemWidth={280}
        gap={16}
        autoScroll={false}
        showControls={true}
        itemsPerView={itemsPerView}
        showDots={true}
      />
    </TFLayoutHomeSection>
  );
}
